import {
    MainCamera,
    FrontCamera,
    TopCamera,
    BottomCamera,
    ComparativeCamera
} from 'Domain/Scene/Provider/CameraProvider'
import { setCameraToLayer } from 'Domain/Scene/Handler/LayerHandler'
import { threeEngine } from 'Domain/Scene/Engine/ThreeEngine'

/*****************************************************************
 * CURRENT CAMERAS UPDATES
 * ***************************************************************/

/**
 * Setup camera
 * @param {object} camera
 * @param {string} name
 * @param {string} view
 * @param {array} param3
 * @param {boolean} up
 * @returns object
 */
const cameraSetup = (camera, name, view, [x, y, z], up = false) => {
    camera.name = name
    camera.view = view
    camera.position.set(x, y, z)
    if (up) camera.up.set(0, 0, 1)
    return camera
}

/**
 * Assign the ratio after any update
 * @param {number} width
 * @param {number} height
 * @param {object} camera
 * @param {boolean} isPortrait
 */
export const InitCameraRatio = (width, height, camera, isPortrait = false) => {
    const aspect = width / Math.max(height, 1)
        const portraitDivisor = isPortrait && (camera.name === 'TopCamera' || camera.name === 'BottomCamera') ? 1 : 1
    const frustumHeightByName = {
        MainCamera: 60,
        ComparativeCamera: 48,
        FrontCamera: 78,
        TopCamera: 78 / portraitDivisor,
        BottomCamera: 78 / portraitDivisor
    }
    const frustumHeight = frustumHeightByName[camera.name] || 160
    const halfH = frustumHeight / 2
    const halfW = halfH * aspect

    camera.left = -halfW
    camera.right = halfW
    camera.top = halfH
    camera.bottom = -halfH
    camera.updateProjectionMatrix()
    setCameraToLayer(camera)
}

/*****************************************************************
 * CAMERA SETUP
 * ***************************************************************/

/**
 * Provide cameras Setup
 * @returns PerspectiveCamera
 */
const
    TopCameraSetup = (x = 0, y = 0, z = 300) => cameraSetup(TopCamera, 'TopCamera', 'top', [x, y, z]),
    BottomCameraSetup = (x = 0, y = 0, z = -300) => {
        const cam = cameraSetup(BottomCamera, 'BottomCamera', 'bottom', [x, y, z]);
        // Flip only bottom view (left panel in double/triple)
        cam.up.set(0, -1, 0);
        return cam;
    },
    FrontCameraSetup = (x = 0, y = -300, z = 0) => cameraSetup(FrontCamera, 'FrontCamera', 'front', [x, y, z], true),
    MainCameraSetup = (x = 0, y = -180, z = 0) => cameraSetup(MainCamera, 'MainCamera', 'main', [x, y, z], true),
    ComparativeCameraSetup = (x = 0, y = -180, z = 0) => cameraSetup(ComparativeCamera, 'ComparativeCamera', 'comparative', [x, y, z], true)

/*****************************************************************
 * MAIN SCENE CAMERA SETUP
 * ***************************************************************/

export const initCameras = () => {
    const nextCameras = {
        main: MainCameraSetup(),
        front: FrontCameraSetup(),
        top: TopCameraSetup(),
        bottom: BottomCameraSetup(),
        comparative: ComparativeCameraSetup()
    };
    threeEngine.setCameras(nextCameras);
    return nextCameras;
};

export const MainCameraPosition = (x, y, z) => MainCameraSetup(x, y, z);
export const FrontCameraPosition = (x, y, z) => FrontCameraSetup(x, y, z);

export const updateComparativeCamera = () => {
    ComparativeCamera.position.copy(MainCamera.position);
    ComparativeCamera.rotation.copy(MainCamera.rotation);
    ComparativeCamera.zoom = MainCamera.zoom;
    ComparativeCamera.left = MainCamera.left;
    ComparativeCamera.right = MainCamera.right;
    ComparativeCamera.top = MainCamera.top;
    ComparativeCamera.bottom = MainCamera.bottom;
    ComparativeCamera.near = MainCamera.near;
    ComparativeCamera.far = MainCamera.far;
    ComparativeCamera.updateProjectionMatrix();
}


export const updateCamera = (cam, orbitControls) => {
    if (!orbitControls || orbitControls.object !== cam) {
        const target = (cam.userData && cam.userData.target) ? cam.userData.target : threeEngine.getCenter();
        if (target) {
            cam.lookAt(target);
        }
    }
};
